import {metrics} from '@opentelemetry/api';
import {
  CompositePropagator,
  W3CBaggagePropagator,
  W3CTraceContextPropagator,
} from '@opentelemetry/core';
import {OTLPMetricExporter} from '@opentelemetry/exporter-metrics-otlp-http';
import {OTLPTraceExporter} from '@opentelemetry/exporter-trace-otlp-http';
import {resourceFromAttributes} from '@opentelemetry/resources';
import {
  MeterProvider,
  PeriodicExportingMetricReader,
} from '@opentelemetry/sdk-metrics';
import {
  BatchSpanProcessor,
  WebTracerProvider,
} from '@opentelemetry/sdk-trace-web';

import {logger} from '@zoefin/logger';
import {
  forceFlushOpenTelemetryLogger,
  initializeOpenTelemetryLogger,
} from '@zoefin/logger/otel';

import {
  OTEL_METRICS_EXPORT_INTERVAL_MS,
  OTEL_METRICS_PATH,
  OTEL_SCHEDULED_DELAY_MS,
  OTEL_TRACE_PATH,
} from '../constants';

import {
  BROWSER_ERROR_DEDUPE_WINDOW_MS,
  BROWSER_ERROR_IGNORED_URL_PATTERNS,
  BROWSER_OTEL_STATE,
  DEFAULT_ENVIRONMENT,
  DEFAULT_SERVICE_NAME,
} from './constants';
import type {ResourceErrorDetails, ResourceErrorTarget} from './types';

const getServiceName = (): string =>
  process.env.NEXT_PUBLIC_OTEL_SERVICE_NAME || DEFAULT_SERVICE_NAME;

const getEnvironment = (): string =>
  process.env.NEXT_PUBLIC_OTEL_SERVICE_ENVIRONMENT || DEFAULT_ENVIRONMENT;

const shouldIgnoreBrowserErrorUrl = (url?: string | null): boolean => {
  if (!url) {
    return false;
  }

  if (
    url.startsWith('about:') ||
    url.startsWith('blob:') ||
    url.startsWith('data:')
  ) {
    return true;
  }

  return BROWSER_ERROR_IGNORED_URL_PATTERNS.some((pattern) =>
    pattern.test(url),
  );
};

const isSameOriginUrl = (url?: string | null): boolean => {
  if (!url || shouldIgnoreBrowserErrorUrl(url)) {
    return false;
  }

  try {
    return (
      new URL(url, window.location.origin).origin === window.location.origin
    );
  } catch {
    return false;
  }
};

// deduplicates browser errors within the configured reporting window.
const shouldReportBrowserError = (key: string): boolean => {
  const now = Date.now();

  for (const [
    existingKey,
    timestamp,
  ] of BROWSER_OTEL_STATE.recentBrowserErrorKeys) {
    if (now - timestamp > BROWSER_ERROR_DEDUPE_WINDOW_MS) {
      BROWSER_OTEL_STATE.recentBrowserErrorKeys.delete(existingKey);
    }
  }

  const lastSeen = BROWSER_OTEL_STATE.recentBrowserErrorKeys.get(key);

  if (lastSeen && now - lastSeen < BROWSER_ERROR_DEDUPE_WINDOW_MS) {
    return false;
  }

  BROWSER_OTEL_STATE.recentBrowserErrorKeys.set(key, now);

  return true;
};

const buildBrowserErrorKey = (
  ...parts: ReadonlyArray<string | number | null | undefined>
): string => parts.map((part) => String(part ?? '')).join('|');

const getResourceErrorDetails = (
  target: ResourceErrorTarget | null,
): ResourceErrorDetails => {
  if (!target) {
    return {};
  }

  return {
    tagName: typeof target.tagName === 'string' ? target.tagName : undefined,
    url: target.currentSrc || target.src || target.href,
  };
};

// reports uncaught runtime errors.
const handleWindowError = (event: Event) => {
  if (event instanceof ErrorEvent) {
    const errorKey = buildBrowserErrorKey(
      'runtime',
      event.message,
      event.filename,
      event.lineno,
      event.colno,
    );

    if (!shouldReportBrowserError(errorKey)) {
      return;
    }

    logger.error(
      {
        category: 'browser-runtime-error',
        columnNumber: event.colno,
        error:
          event.error instanceof Error
            ? {
                message: event.error.message,
                name: event.error.name,
                stack: event.error.stack,
              }
            : undefined,
        filename: event.filename,
        lineNumber: event.lineno,
        message: event.message,
        pathname: window.location.pathname,
      },
      'Unhandled browser error',
    );

    return;
  }

  const {tagName, url} = getResourceErrorDetails(
    event.target as ResourceErrorTarget | null,
  );

  if (!isSameOriginUrl(url)) {
    return;
  }

  const errorKey: string = buildBrowserErrorKey('resource', tagName, url);

  if (!shouldReportBrowserError(errorKey)) {
    return;
  }

  logger.error(
    {
      category: 'browser-resource-error',
      pathname: window.location.pathname,
      resourceTagName: tagName,
      resourceUrl: url,
    },
    'Browser resource failed to load',
  );
};

// reports unhandled promise rejections.
const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
  const reason =
    event.reason instanceof Error ? event.reason.message : String(event.reason);
  const errorKey = buildBrowserErrorKey('rejection', reason);

  if (!shouldReportBrowserError(errorKey)) {
    return;
  }

  logger.error(
    {
      category: 'browser-unhandled-rejection',
      pathname: window.location.pathname,
      reason: event.reason,
    },
    'Unhandled promise rejection in browser',
  );
};

// reports security policy violations.
const handleSecurityPolicyViolation = (event: SecurityPolicyViolationEvent) => {
  const blockedUri = event.blockedURI || undefined;
  const sourceFile = event.sourceFile || undefined;

  if (
    shouldIgnoreBrowserErrorUrl(blockedUri) &&
    shouldIgnoreBrowserErrorUrl(sourceFile)
  ) {
    return;
  }

  const errorKey = buildBrowserErrorKey(
    'csp',
    event.effectiveDirective,
    blockedUri,
    sourceFile,
    event.lineNumber,
    event.columnNumber,
  );

  if (!shouldReportBrowserError(errorKey)) {
    return;
  }

  logger.error(
    {
      blockedUri,
      category: 'browser-security-policy-violation',
      columnNumber: event.columnNumber,
      disposition: event.disposition,
      documentUri: event.documentURI,
      effectiveDirective: event.effectiveDirective,
      lineNumber: event.lineNumber,
      originalPolicy: event.originalPolicy,
      pathname: window.location.pathname,
      sourceFile,
      statusCode: event.statusCode,
      violatedDirective: event.violatedDirective,
    },
    'Browser security policy violation',
  );
};

// flushes buffered telemetry spans, metrics, and logs.
const flushTelemetry = () => {
  BROWSER_OTEL_STATE.provider?.forceFlush().catch((error: unknown) => {
    logger.error(error, 'Failed to flush browser telemetry spans');
  });
  BROWSER_OTEL_STATE.meterProvider?.forceFlush().catch((error: unknown) => {
    logger.error(error, 'Failed to flush browser telemetry metrics');
  });
  forceFlushOpenTelemetryLogger().catch((error: unknown) => {
    logger.error(error, 'Failed to flush browser telemetry logs');
  });
};

// flushes buffered telemetry spans and logs when the tab is transitioning to the background.
const handleVisibilityChange = () => {
  if (document.visibilityState === 'hidden') {
    flushTelemetry();
  }
};

// attaches the browser lifecycle and error listeners.
const attachGlobalListeners = () => {
  if (BROWSER_OTEL_STATE.listenersAttached) {
    return;
  }

  document.addEventListener('visibilitychange', handleVisibilityChange);
  document.addEventListener(
    'securitypolicyviolation',
    handleSecurityPolicyViolation,
  );
  window.addEventListener('error', handleWindowError, true);
  window.addEventListener('unhandledrejection', handleUnhandledRejection);
  window.addEventListener('pagehide', flushTelemetry);
  window.addEventListener('beforeunload', flushTelemetry);
  BROWSER_OTEL_STATE.listenersAttached = true;
};

// detaches the browser lifecycle and error listeners.
const detachGlobalListeners = () => {
  if (!BROWSER_OTEL_STATE.listenersAttached) {
    return;
  }

  document.removeEventListener('visibilitychange', handleVisibilityChange);
  document.removeEventListener(
    'securitypolicyviolation',
    handleSecurityPolicyViolation,
  );
  window.removeEventListener('error', handleWindowError, true);
  window.removeEventListener('unhandledrejection', handleUnhandledRejection);
  window.removeEventListener('pagehide', flushTelemetry);
  window.removeEventListener('beforeunload', flushTelemetry);
  BROWSER_OTEL_STATE.listenersAttached = false;
};

// ensures the browser trace and meter providers are initialized.
const ensureProviders = () => {
  if (BROWSER_OTEL_STATE.provider) {
    return;
  }

  const resource = resourceFromAttributes({
    'service.name': getServiceName(),
    'deployment.environment': getEnvironment(),
  });

  // Does not auto-instrument fetch — patching global fetch broke Next App Router
  // navigation requests by injecting tracing behavior into framework-owned requests.
  const traceExporter = new OTLPTraceExporter({
    url: `${window.location.origin}${OTEL_TRACE_PATH}`,
  });

  BROWSER_OTEL_STATE.provider = new WebTracerProvider({
    resource,
    spanProcessors: [
      new BatchSpanProcessor(traceExporter, {
        scheduledDelayMillis: OTEL_SCHEDULED_DELAY_MS,
      }),
    ],
  });

  BROWSER_OTEL_STATE.provider.register({
    propagator: new CompositePropagator({
      propagators: [
        new W3CTraceContextPropagator(),
        new W3CBaggagePropagator(),
      ],
    }),
  });

  const metricExporter = new OTLPMetricExporter({
    url: `${window.location.origin}${OTEL_METRICS_PATH}`,
  });

  BROWSER_OTEL_STATE.meterProvider = new MeterProvider({
    resource,
    readers: [
      new PeriodicExportingMetricReader({
        exporter: metricExporter,
        exportIntervalMillis: OTEL_METRICS_EXPORT_INTERVAL_MS,
      }),
    ],
  });

  metrics.setGlobalMeterProvider(BROWSER_OTEL_STATE.meterProvider);
};

// initializes the browser telemetry logger.
const initializeBrowserLogger = () => {
  initializeOpenTelemetryLogger({
    collectorHost: window.location.origin,
    environment: getEnvironment(),
    serviceName: getServiceName(),
  }).catch((error: unknown) => {
    logger.error(error, 'Failed to initialize browser telemetry logger');
  });
};

// mounts the browser OTEL singleton lifecycle.
export const mountBrowserOtel = () => {
  BROWSER_OTEL_STATE.activeMounts += 1;

  // Skip redundant initialization on Strict Mode remounts.
  if (BROWSER_OTEL_STATE.activeMounts > 1) {
    return;
  }

  ensureProviders();
  initializeBrowserLogger();
  attachGlobalListeners();
};

// unmounts the browser OTEL singleton lifecycle.
export const unmountBrowserOtel = () => {
  BROWSER_OTEL_STATE.activeMounts = Math.max(
    0,
    BROWSER_OTEL_STATE.activeMounts - 1,
  );

  if (BROWSER_OTEL_STATE.activeMounts === 0) {
    detachGlobalListeners();
  }
};
